import { getUriQueryParameter } from '@impuls-coach/util-get-uri-query-parameter';
import { isNumber, isObject } from '@impuls-coach/util-is';
import { error, group, groupEnd, warn } from 'helper/debug';
import appConfig from 'helper/mapBackendConfig';
import { template } from 'helper/string';
import { delayed } from 'lib/Job';

const win: any = window;
let missingLabels: string[] = [];
let startedDelayOnLog = false;

const ENV = process.env.NODE_ENV || 'production';
export const QUERY_DEBUGGING_ENABLED = !!getUriQueryParameter('i18nDebug');
export const MAGNOLIA_DEBUGGING_ENABLED = !!appConfig.env.i18nDebuggingEnabled;

if (!win.ReactApp || !win.ReactApp.I18n) {
	error(`Something went wrong within label translation, unable to populate labels.`);
}

/*
 * Check if an i18n translation label with <key> exists.
 * @param {string} key
 */
export const labelExists = (key: string): boolean => {
	return !!win.ReactApp.I18n[key];
};

type i18nDataSet = {
	[key: string]: any;
};

type i18nDataSetPrivate = i18nDataSet & {
	__disableHtmlEncoding?: boolean;
};

/*
 * Define blacklisted keys for debugging, for example
 * routes should not be passed back as raw keys.
 * @type {Array<RegExp>}
 */
const debuggingBlacklistKeys: RegExp[] = [/^(react.impulsCoach.routes)(.*)(.href)$/g];

/*
 * Check if a key is blacklisted by the <debuggingBlacklistKeys>
 * expressions above. Will return true if any matched.
 * @param {string} key
 * @return {boolean}
 */
/* istanbul ignore next */
const isKeyDebuggingBlacklisted = (key: string): boolean => {
	const tests: boolean[] = debuggingBlacklistKeys.map((blacklistExpr: RegExp) => {
		return blacklistExpr.test(key);
	});

	return tests.some((isBlacklisted: boolean) => !!isBlacklisted);
};

/*
 * Translation method supporting templating and pluralization
 *
 *  e.g. i18n("my.test") => my.test
 *  e.g. i18n("my.test", null, 2) => my.test.plural
 *  e.g. i18n("my.test", null, 1) => my.test.singular
 *  e.g. i18n("my.test", { a: 10 }) => my.test (hey {a}) => hey 10
 *
 * @param {string} key                      Unique key for label stored inside window object
 * @param {Object?} dataSet                 Pass valid object for template compilation
 * @param {number?} pluralizationCount      Pass valid number for pluralization
 * @return {string}
 */
const i18n = (key: string, dataSet?: i18nDataSet | i18nDataSetPrivate | null, pluralizationCount?: number): string => {
	const originalKey = key;

	if (MAGNOLIA_DEBUGGING_ENABLED && QUERY_DEBUGGING_ENABLED && !isKeyDebuggingBlacklisted(key)) {
		return key;
	}

	if (isNumber(pluralizationCount)) {
		key = `${key}.${pluralizationCount === 1 ? 'singular' : 'plural'}`;

		if (!labelExists(key)) {
			missingLabels.push(`${key}`);
			key = originalKey;
		}
	}

	const str = win.ReactApp.I18n[key];

	if (!startedDelayOnLog && missingLabels.length > 0) {
		startedDelayOnLog = true;

		delayed(() => {
			group(`Missing labels (${missingLabels.length})`);
			missingLabels.forEach((missingKey) => warn(`Missing label for "${missingKey}"`));
			groupEnd(`Missing labels (${missingLabels.length})`);
			missingLabels = [];
		}, 2000);
	}

	if (!str && key.indexOf('.meta.') < 0) {
		return '';
	}

	if (!str) {
		if (ENV === 'production') {
			missingLabels.push(`${key}`);
		} else {
			warn(`Undefined label ${key}`);
		}

		return '';
	}

	if (dataSet && isObject(dataSet)) {
		return template(str, dataSet, !!dataSet.__disableHtmlEncoding);
	}

	return `${str}`;
};

export const pluralization = (amount: number, singular: string, plural: string): string => {
	if (amount === 0 || amount > 1) {
		return plural;
	}

	return singular;
};

export default i18n;
